import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockContainer from '../BlockContainer';
import ListingCarousel from '../../SectionBuilder/SectionCarousel/ListingCarousel/ListingCarousel';

import css from './BlockDefault.module.css';
import FAQAccordion from '../../../../components/CustomComponents/FAQAccordion/FAQAccordion';
import { ListingCard, NamedLink } from '../../../../components';
import { FormattedMessage, useIntl } from 'react-intl';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { useSelector } from 'react-redux';
import { customListingsSelector } from '../../../LandingPage/LandingPage.duck';
import { useViewportSize } from '../../../../hooks';

const HERO_POSTER_SRC = '/static/images/playing-vincyle.png';
const HERO_ANIMATED_SRC =
  'https://res.cloudinary.com/dajwmuczg/image/upload/v1774871700/record-loop_11-ezgif.com-optimize_pgpgqv.gif';

const FieldMedia = props => {
  const { className, media, sizes, options, loading = 'lazy', fetchPriority = 'auto' } = props;
  const hasMediaField = hasDataInFields([media], options);
  return hasMediaField ? (
    <div className={classNames(className, css.media)}>
      <Field
        data={media}
        sizes={sizes}
        options={options}
        loading={loading}
        fetchPriority={fetchPriority}
      />
    </div>
  ) : null;
};

const HeroAnimatedMedia = () => {
  const [shouldAnimateHero, setShouldAnimateHero] = useState(false);

  useEffect(() => {
    // Delay GIF load slightly so the poster image LCP is not blocked
    const timeoutId = window.setTimeout(() => setShouldAnimateHero(true), 1000);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <img
      src={HERO_ANIMATED_SRC}
      // src={shouldAnimateHero ? HERO_ANIMATED_SRC : HERO_POSTER_SRC}
      alt="playing vinyl"
      className={css.heroBannerVideo}
      loading="eager"
      decoding="async"
      fetchPriority={shouldAnimateHero ? 'low' : 'high'}
    />
  );
};

const FeaturedListingsSection = ({ featuredListings, intl }) => {
  const viewportSize = useViewportSize();
  const fallbackListings = useSelector(state => customListingsSelector(state, 'featured-listings'));

  const featuredToRender =
    featuredListings && featuredListings.length > 0 ? featuredListings : fallbackListings || [];
  const isMobile = viewportSize.width < 768;

  return (
    <div>
      <div className={css.featureListingHeader}>
        <h4>
          <FormattedMessage id="home.featurelistingheading" />
        </h4>
        <NamedLink
          className={classNames(css.viewAllBtn, css.desktopBtn)}
          name="SearchPage"
          to={{ search: '?pub_featured=true' }}
        >
          <FormattedMessage id="home.featurelistingBtnText" /> <IconCard brand="rightArrow" />
        </NamedLink>
      </div>
      {isMobile ? (
        <ListingCarousel
          listings={featuredToRender.slice(0, 4)}
          slidesToShow={1.3}
          showArrows={false}
          showDots={true}
          className={css.featuredCarousel}
          emblaSettings={{ loop: false, align: 'start' }}
          showFeaturedTag={true}
        />
      ) : (
        <div className={css.featureListingGrid}>
          {(featuredToRender || []).slice(0, 4).map((i, index) => (
            <ListingCard
              key={i?.id?.uuid || i?.id || i?.uuid || index}
              listing={i}
              intl={intl}
            />
          ))}
        </div>
      )}

      <NamedLink
        className={classNames(css.viewAllBtn, css.mobileBtn)}
        name="SearchPage"
        to={{ search: '?pub_featured=true' }}
      >
        <FormattedMessage id="home.featurelistingBtnText" /> <IconCard brand="rightArrow" />
      </NamedLink>
    </div>
  );
};

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This returns a component that can render 'defaultBlock' config.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.blockId id from the block config
 * @param {string} props.blockName name from the block config (not used)
 * @param {'defaultBlock'} props.blockType blockType is set to 'defaultBlock'
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.className add more styles in addition to components own css.root
 * @param {string?} props.mediaClassName add styles for the block's attached media field
 * @param {string?} props.textClassName add styles for the block's attached text field
 * @param {string?} props.ctaButtonClass add styles for the block's attached CTA field
 * @param {Object?} props.title heading config for the block
 * @param {Object?} props.text content config for the block (can be markdown)
 * @param {Object?} props.callToAction call to action button (e.g. internal link config)
 * @param {string?} props.responsiveImageSizes
 * @param {Object} props.options extra options for the block component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents Custom fieldComponents
 * @returns {JSX.Element} component that renders block type: 'defaultBlock'
 */
const BlockDefault = props => {
  const {
    blockId,
    className,
    rootClassName,
    mediaClassName,
    textClassName,
    ctaButtonClass,
    title,
    text,
    callToAction,
    media,
    responsiveImageSizes,
    options,
    alignment,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const hasTextComponentFields = hasDataInFields([title, text, callToAction], options);

  const alignmentClasses = {
    left: css.alignLeft,
    center: css.alignCenter,
    right: css.alignRight,
  };

  const alignmentClass = alignmentClasses[alignment];
  const intl = useIntl();
  const isPriorityMediaBlock = blockId === 'hero-left-images-container';

  return (
    <BlockContainer id={blockId} className={classes}>
      <FieldMedia
        media={media}
        sizes={responsiveImageSizes}
        className={mediaClassName}
        options={options}
        loading={isPriorityMediaBlock ? 'eager' : 'lazy'}
        fetchPriority={isPriorityMediaBlock ? 'high' : 'auto'}
      />
      {hasTextComponentFields ||
      blockId === 'faq-box' ||
      blockId === 'featurelisting-left-section' ||
      blockId === 'marquee-text' ? (
        <>
          {blockId === 'home-how-groovebay-works-card-1' && (
            <img src="/static/images/list.gif" className={css.gifImage} alt="gif" loading="lazy" />
          )}
          {blockId === 'home-how-groovebay-works-card-2' && (
            <img src="/static/images/cart.gif" className={css.gifImage} alt="gif" loading="lazy" />
          )}
          {blockId === 'home-how-groovebay-works-card-3' && (
            <img src="/static/images/src.gif" className={css.gifImage} alt="gif" loading="lazy" />
          )}

          <div className={classNames(textClassName, alignmentClass, css.text)}>
            <Field data={title} options={options} />
            <Field data={text} options={options} />
            <Field data={callToAction} className={ctaButtonClass} options={options} />
            {blockId === 'faq-box' && <div id="home-faq-section" />}
            {blockId === 'faq-box' && <FAQAccordion />}
            {blockId === 'featurelisting-left-section' && (
              <FeaturedListingsSection featuredListings={options?.featuredListings} intl={intl} />
            )}
          </div>
          {/* {
            blockId === 'featurelisting-shop-now-section' && currentLanguage.startsWith('nl') && (
              <img className={css.featurelistingShopNowSectionImage} src='/static/images/girl-looking-vinyle.png' />
            )
          } */}
          {blockId === 'hero-right-image-and-content-container' && <HeroAnimatedMedia />}
          {blockId === 'marquee-text' && (
            <div className={css.marqueeWrapper}>
              <div className={css.marqueText}>
                <h4>
                  <FormattedMessage id="home.marqueeText" />
                </h4>
                <IconCard brand="whiteVinyle" />
                <h4>
                  <FormattedMessage id="home.marqueeText" />
                </h4>

                {/* duplicate for seamless loop */}
                <IconCard brand="whiteVinyle" />
                <h4>
                  <FormattedMessage id="home.marqueeText" />
                </h4>
                <IconCard brand="whiteVinyle" />
                <h4>
                  <FormattedMessage id="home.marqueeText" />
                </h4>

                {/* duplicate for seamless loop */}
                <IconCard brand="whiteVinyle" />
                <h4>
                  <FormattedMessage id="home.marqueeText" />
                </h4>
                <IconCard brand="whiteVinyle" />
                <h4>
                  <FormattedMessage id="home.marqueeText" />
                </h4>
              </div>
            </div>
          )}
        </>
      ) : null}
    </BlockContainer>
  );
};

export default BlockDefault;
